import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Stethoscope } from "lucide-react";

const EMPTY = {
  name: "", degree: "", council_no: "", clinic_name: "", clinic_address: "",
  phone: "", email: "", company: "",
};

const FIELDS = [
  ["name", "Full name", "Dr. Asha Patel", "text"],
  ["degree", "Degree", "BAMS, MD (Ayu)", "text"],
  ["council_no", "Council registration number", "GUJ/12345", "text"],
  ["clinic_name", "Clinic name", "Shree Ayurveda Clinic", "text"],
  ["phone", "Mobile number", "98765 43210", "tel"],
  ["email", "Email", "you@clinic.com", "email"],
];

/**
 * Public page: a doctor asks to join the referral programme. Nothing is issued here —
 * the clinic reviews the application and a referral code is created on approval.
 */
export default function DoctorPartner() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.council_no.trim() || !form.phone.trim() || !form.email.trim())
      return toast.error("Name, council number, mobile and email are needed");
    setBusy(true);
    try {
      await api.post("/doctor-partners", form);
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <main className="max-w-xl mx-auto px-5 py-20" data-testid="partner-apply-done">
        <Card className="border-primary/30">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="mx-auto text-primary mb-4" size={32} />
            <h1 className="font-display text-3xl mb-2">Thank you, doctor</h1>
            <p className="text-base text-muted-foreground">
              Your application has reached the clinic. Once it is approved you will receive your own
              referral code, a QR poster for your clinic and a login to follow your referrals.
            </p>
          </CardContent>
        </Card>
      </main>
    );

  return (
    <main className="max-w-2xl mx-auto px-5 py-14" data-testid="doctor-partner-page">
      <p className="overline flex items-center gap-2"><Stethoscope size={14} /> Doctor partner programme</p>
      <h1 className="font-display text-4xl sm:text-5xl mt-2 mb-3">Refer your patients, keep the credit</h1>
      <p className="text-base text-muted-foreground mb-10 max-w-xl">
        Send your patients to us with your own QR code and link. Every prescription, appointment and
        order that arrives through it is recorded against your name — you can follow all of it from
        your own login.
      </p>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            {FIELDS.map(([key, label, placeholder, type]) => (
              <div key={key} className={key === "clinic_name" ? "sm:col-span-2" : ""}>
                <label className="overline">{label}</label>
                <Input
                  type={type}
                  value={form[key]}
                  onChange={set(key)}
                  placeholder={placeholder}
                  className="mt-2 bg-transparent"
                  data-testid={`partner-${key}`}
                />
              </div>
            ))}
          </div>
          <div>
            <label className="overline">Clinic address</label>
            <Textarea
              value={form.clinic_address}
              onChange={set("clinic_address")}
              placeholder="Street, area, city, pincode"
              className="mt-2 bg-transparent"
              rows={3}
              data-testid="partner-clinic_address"
            />
          </div>
          {/* Honeypot: a real doctor never sees this. */}
          <input
            value={form.company}
            onChange={set("company")}
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />
          <Button disabled={busy} className="btn-action h-12 px-8" onClick={submit} data-testid="partner-submit">
            {busy ? "Sending…" : "Apply to join"}
          </Button>
          <p className="text-xs text-muted-foreground">
            We verify every council registration number before approving an application.
          </p>
        </CardContent>
      </Card>
    </main>
  );
}
